// A search's filling rule, and whatever fills in step with it (the term, or
// the item being opened), as an honest loading bar: it runs while the next
// page gets ready and finishes when it is, the owner's call (2026-09-30):
// "slow down loading if need be ... to make it more natural ... nothing corny
// just normal". `work` is that readiness (the catalog's data for a search,
// the piece's first photo for an item); the caller passes the promise.
//
// While `work` runs, --search-fill on `el` eases toward 90% and slows the
// longer it takes, the way a real loader creeps near the end. Once `work`
// settles (and at least `minMs` has passed, so a warm cache still shows a
// sweep rather than a blink), it runs on to 100% from wherever it is, picking
// up at its current speed, then holds at full for `hold` and calls `onDone`.
// If `work` outlasts `maxWait` it finishes anyway; the page then loads on its
// own. Every value is set per animation frame, so it never jumps or runs
// backwards. With reduced motion there is no sweep: it goes to full once
// `work` settles. Everything that fills reads the one custom property, so
// they move as one. Returns a function that cancels it.
//
// It replaced a ticker that jumped a random 0.5-15% every 20-100ms, then a
// fixed 0.9s sweep that knew nothing about the load.
export const SEARCH_FILL_MIN_MS = 350;
export const SEARCH_FILL_FINISH_MS = 260;
export const SEARCH_FILL_HOLD_MS = 120;
export const SEARCH_FILL_MAX_WAIT_MS = 6000;

// The approach while loading: 90% at most, 1 - 1/e of the way there after
// APPROACH_MS.
const APPROACH_TO = 0.9;
const APPROACH_MS = 400;

export function runSearchFill(el, work, onDone, {
  minMs = SEARCH_FILL_MIN_MS,
  finishMs = SEARCH_FILL_FINISH_MS,
  hold = SEARCH_FILL_HOLD_MS,
  maxWait = SEARCH_FILL_MAX_WAIT_MS,
} = {}) {
  let raf = 0;
  let timer = 0;
  let cancelled = false;
  let ready = false;
  const set = (p) => {
    if (el) el.style.setProperty("--search-fill", (p * 100).toFixed(2) + "%");
  };
  const giveUp = setTimeout(() => { ready = true; }, maxWait);
  const settled = Promise.resolve(work).catch(() => {}).then(() => { ready = true; });
  const done = () => {
    clearTimeout(giveUp);
    set(1);
    timer = setTimeout(() => {
      if (!cancelled) onDone();
    }, hold);
  };
  const cancel = () => {
    cancelled = true;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
    clearTimeout(giveUp);
  };

  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (!el || reduce) {
    Promise.race([settled, new Promise((r) => setTimeout(r, maxWait))]).then(() => {
      if (!cancelled) done();
    });
    return cancel;
  }

  set(0);
  const start = performance.now();
  // Once ready: where the fill was, how fast it was going (per ms), and when.
  let finish = null;
  const approach = (t) => APPROACH_TO * (1 - Math.exp(-t / APPROACH_MS));
  const frame = (now) => {
    // A frame's timestamp is when the frame began, which can fall a moment
    // before start; clamped, so the first value is never below zero.
    const t = Math.max(0, now - start);
    if (!finish && ready && t >= minMs) {
      finish = { at: now, p: approach(t), v: (APPROACH_TO / APPROACH_MS) * Math.exp(-t / APPROACH_MS) };
    }
    if (!finish) {
      set(approach(t));
      raf = requestAnimationFrame(frame);
      return;
    }
    // The run to full: a cubic from where it was, at the speed it had, to
    // 100% at rest, so the handover shows no kink.
    const s = Math.min(1, (now - finish.at) / finishMs);
    const h00 = 2 * s ** 3 - 3 * s ** 2 + 1;
    const h10 = s ** 3 - 2 * s ** 2 + s;
    const h01 = -2 * s ** 3 + 3 * s ** 2;
    set(Math.min(1, h00 * finish.p + h10 * finish.v * finishMs + h01));
    if (s < 1) raf = requestAnimationFrame(frame);
    else done();
  };
  raf = requestAnimationFrame(frame);
  return cancel;
}
